import { el, clear } from "./dom.js";
import {
	ENGAGEMENT_STATUSES,
	AWARDS,
	statusLabel,
} from "./status-badge.js";
import { FACET_TYPE_LABELS, facetValueLabel, personFacetOptions, tagFacetOptions, airingFacetOptions } from "../utils/filters.js";

// Space-efficient list filter: a "+ Filter" button plus a row of removable
// active-filter chips. The button opens a grouped, searchable multi-select panel
// (Engagement / Ownership / Awards). Only the *active* filters take space, so an
// unfiltered list shows nothing but the button — and it scales to any number of
// awards without a wall of chips.
//
// Filters are { type, value } (see utils/filters.js). status/ownership/award are
// offered in the panel; genre/platform/decade can arrive pre-set (e.g. from a
// Stats deep-link) and still render as removable chips.
//
// opts:
//   mediaType          "videogame" | "book" — for media-aware labels
//   ownershipStatuses  ownership values to offer (games vs books differ)
//   initial            array of { type, value } to start with
//   onChange(filters)  called whenever the active set changes
//
// Returns { button, panel, chips, getFilters } so the caller can place the button
// beside the Sort control and the panel/chips below it.
export function filterBar({
	mediaType,
	ownershipStatuses,
	initial = [],
	onChange,
}) {
	const keyOf = (f) => `${f.type}:${f.value}`;
	let active = dedupe(initial);

	const groups = [
		{
			type: "status",
			label: "Engagement",
			options: ENGAGEMENT_STATUSES.map((v) => ({
				value: v,
				label: statusLabel(v, mediaType),
			})),
		},
		{
			type: "ownership",
			label: "Ownership",
			options: ownershipStatuses.map((v) => ({
				value: v,
				label: statusLabel(v, mediaType),
			})),
		},
		{
			type: "award",
			label: "Awards",
			options: AWARDS.map((a) => ({ value: a.value, label: a.label })),
		},
		{
			type: "edited",
			label: "Corrections",
			options: [{ value: "yes", label: "Edited by you" }],
		},
		{ type: "airing", label: "On the air", options: airingFacetOptions(mediaType) },
		{ type: "tag", label: "Your tags", options: tagFacetOptions(mediaType) },
		// People you follow in this category (their known works).
		{ type: "person", label: "People you follow", options: personFacetOptions(mediaType) },
	].filter((g) => g.options.length);

	const button = el("button.btn.btn--small.filter-toggle", {
		type: "button",
		onclick: () => {
			panel.hidden = !panel.hidden;
			if (!panel.hidden) {
				renderPanel();
				queueMicrotask(() => search.focus());
			}
		},
	});

	const chips = el("div.filter-active", { hidden: true });

	const search = el("input.picker__input", {
		type: "search",
		placeholder: "Find a filter…",
		autocomplete: "off",
		autocapitalize: "off",
		spellcheck: false,
	});
	const groupsWrap = el("div.filter-panel__groups", {});
	const panel = el("div.picker.filter-panel", { hidden: true }, search, groupsWrap);
	search.addEventListener("input", renderPanel);

	function isActive(type, value) {
		return active.some((f) => f.type === type && f.value === value);
	}

	function commit(next) {
		active = dedupe(next);
		renderChips();
		if (!panel.hidden) renderPanel(); // reflect selection in open panel
		onChange(active.slice());
	}

	function toggle(type, value) {
		if (isActive(type, value)) {
			commit(active.filter((f) => !(f.type === type && f.value === value)));
		} else {
			commit([...active, { type, value }]);
		}
	}

	function renderChips() {
		clear(chips);
		button.textContent = active.length ? `Filters · ${active.length}` : "+ Filter";
		if (active.length === 0) {
			chips.hidden = true;
			return;
		}
		chips.hidden = false;
		for (const f of active) {
			const chip = el(
				"div.chip.chip--active.filter-chip",
				{},
				el(
					"span.chip__label",
					{},
					`${FACET_TYPE_LABELS[f.type]}: ${facetValueLabel(f, mediaType)}`,
				),
			);
			chip.append(
				el(
					"button.chip__remove",
					{
						type: "button",
						title: "Remove filter",
						onclick: () => toggle(f.type, f.value),
					},
					"×",
				),
			);
			chips.append(chip);
		}
		chips.append(
			el(
				"button.filter-clear",
				{ type: "button", onclick: () => commit([]) },
				"Clear all",
			),
			// Smart collections (roadmap #56): keep this filter as a live collection.
			el(
				"button.filter-clear",
				{ type: "button", onclick: () => saveSmart(active.slice()) },
				"★ Save as collection",
			),
		);
	}

	function renderPanel() {
		clear(groupsWrap);
		const q = search.value.trim().toLowerCase();
		for (const g of groups) {
			const opts = q
				? g.options.filter((o) => o.label.toLowerCase().includes(q))
				: g.options;
			if (opts.length === 0) continue;
			groupsWrap.append(el("div.filter-panel__label", {}, g.label));
			const row = el("div.chips", {});
			for (const o of opts) {
				const selected = isActive(g.type, o.value);
				row.append(
					el(
						`button.chip${selected ? ".chip--active" : ""}`,
						{
							type: "button",
							"aria-pressed": selected ? "true" : "false",
							onclick: () => toggle(g.type, o.value),
						},
						o.label,
					),
				);
			}
			groupsWrap.append(row);
		}
		if (!groupsWrap.children.length) {
			groupsWrap.append(el("div.picker__empty", {}, "No matching filters."));
		}
	}

	async function saveSmart(filters) {
		const suggested = filters.map((f) => facetValueLabel(f, mediaType)).join(" + ");
		const name = window.prompt("Name this smart collection — it fills itself from the filter:", suggested);
		if (!name || !name.trim()) return;
		const { createManualGroup } = await import("../db/groups.js");
		const { navigate } = await import("../main.js");
		const group = await createManualGroup({
			name: name.trim(),
			media_type: mediaType,
			smart_rule: { type: "smart", media_type: mediaType, filters },
		});
		navigate(`/groups/${group.id}`);
	}

	renderChips();
	return { button, panel, chips, getFilters: () => active.slice() };
}

function dedupe(list) {
	const seen = new Set();
	const out = [];
	for (const f of list ?? []) {
		const k = `${f.type}:${f.value}`;
		if (!seen.has(k)) {
			seen.add(k);
			out.push(f);
		}
	}
	return out;
}
